import type { VoidFunctionComponent } from 'react';

import { VulnEpssType } from '../../../../../shared/issuesV2';
import FilterCardRadios from '../../../generic/Filter/FilterCardRadios';
import FilterPanel from '../../../generic/Filter/FilterPanel';
import FormLabel from '../../../generic/Forms/FormLabel';
import RangeSlider from '../../../generic/Forms/RangeSlider';
import type { EpssFilter } from '../../../../../shared/issuesV2';
import type { SelectOption } from '../../../../types';
import type { RangeTuple } from '../../../generic/Forms/RangeSlider';

const FILTER_OPTIONS: Array<SelectOption<VulnEpssType>> = [
  { value: VulnEpssType.Score, label: 'Score' },
  { value: VulnEpssType.Percentile, label: 'Percentile' },
];

const defaultFilter: EpssFilter = {
  type: VulnEpssType.Score,
  min: 0,
  max: 1,
};

const filterIsDefault = (filter: Partial<EpssFilter>) => (
  filter?.min === defaultFilter.min && filter?.max === defaultFilter.max && filter?.type === defaultFilter.type
);

//  Takes a RangeTuple and converts the values to percentiles (ie: 12.3 becomes 0.123)
const convertToPercentile = ([min, max]: RangeTuple): Pick<EpssFilter, 'min' | 'max'> => ({
  min: min / 100,
  max: max / 100,
});

//  Takes a RangeTuple and converts the values from percentiles to actual values (ie: 0.123 becomes 12.3)
const convertFromPercentile = ([min, max]: RangeTuple): RangeTuple => [
  //  TODO we may need some rounding, will report back
  min * 100,
  max * 100,
];

type Props = {
  filter?: EpssFilter;
  /**
   * Callback function to be called when a filter option is selected/unselected.
   */
  onChange: (filter: EpssFilter | null) => void;
};

/**
 * EPSSRangeFilter allows the user to select a range of EPSS values to filter by.
 * It provides RangeSlider component for actually selecting the value, and then
 * a pair of radio buttons to allow the user to toggle between EPSS "score" and "percentile".
 */
const EPSSRangeFilter: VoidFunctionComponent<Props> = ({
  filter,
  onChange,
}) => {
  //  Set the values, falling back to the default filter if the filter is undefined.
  //  Converts the values to percentiles for the RangeSlider component.
  const rangeValue: RangeTuple = convertFromPercentile(
    //  If a filter is provided, we use its values, otherwise we use the default filter values.
    filter
      ? [filter.min, filter.max]
      : [defaultFilter.min, defaultFilter.max],
  );
  const radioValue: VulnEpssType = filter?.type || defaultFilter.type;

  const onUpdate = (updatedFilter: Partial<EpssFilter>) => {
    //  If the filter is being reset to default, we pass `null` to the callback.
    if (filterIsDefault(updatedFilter)) {
      return onChange(null);
    }
    //  Otherwise, we update the filter (falling back to default in case filter is undefined)
    return onChange({
      ...(filter || defaultFilter),
      ...updatedFilter,
    });
  };

  const onRadioChange = (value: VulnEpssType) => onUpdate({ type: value });
  const onRangeChange = (value: RangeTuple) => onUpdate(convertToPercentile(value));

  return (
    <FilterPanel>
      <FormLabel label="EPSS Range">
        <div className="margin-top-sm">
          <RangeSlider
            id="issues-inbox-epss-range-filter"
            min={0}
            max={100}
            step={0.01}
            value={rangeValue}
            onChange={onRangeChange}
          />
        </div>
        <div className="margin-top-sm">
          <FilterCardRadios
            label=""
            options={FILTER_OPTIONS}
            value={radioValue}
            onChange={onRadioChange}
          />
        </div>
      </FormLabel>
    </FilterPanel>
  );
};

export default EPSSRangeFilter;
